// ============================================================================
// ARBITRATOR FORM MODAL — Add / Edit Arbitrator (MSTR-ARB-001)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { arbitratorService } from 'api/services/arbitratorService';
import type { Arbitrator } from 'api/types/arbitrator.types';

type ArbitratorStatus = 'ACTIVE' | 'INACTIVE';

interface ArbitratorFormData {
  arbitratorCode: string;
  arbitratorName: string;
  panelName: string;
  empanelledOn: string;     // YYYY-MM-DD for <input type="date">
  email: string;
  phoneNumber: string;
  statusCode: ArbitratorStatus;
}

const INITIAL_FORM_DATA: ArbitratorFormData = {
  arbitratorCode: '',
  arbitratorName: '',
  panelName: '',
  empanelledOn: '',
  email: '',
  phoneNumber: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const dateOnly = (iso?: string | null) => (iso ? iso.split('T')[0] : '');

export interface ArbitratorFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: Arbitrator | null;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const ArbitratorFormModal: React.FC<ArbitratorFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<ArbitratorFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: ArbitratorFormData = rec
      ? {
          arbitratorCode: rec.arbitratorCode,
          arbitratorName: rec.arbitratorName,
          panelName: rec.panelName ?? '',
          empanelledOn: dateOnly(rec.empanelledOn),
          email: rec.email ?? '',
          phoneNumber: rec.phoneNumber ?? '',
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as ArbitratorStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.arbitratorCode || currentFormData.arbitratorCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.arbitratorCode?.trim();
      const name = currentFormData.arbitratorName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.arbitratorCode || currentRecord?.arbitratorCode || 'Arbitrator'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.arbitratorCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.arbitratorCode.trim();
    const name = formData.arbitratorName.trim();
    const email = formData.email.trim();

    if (!code) errors.arbitratorCode = 'Arbitrator code is required';
    else if (code.length > 30) errors.arbitratorCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.arbitratorCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.arbitratorName = 'Arbitrator name is required';
    else if (name.length > 200) errors.arbitratorName = 'Max 200 characters';

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Invalid email format';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.arbitratorCode.trim().toUpperCase();
      const name = formData.arbitratorName.trim();

      const payload = {
        arbitratorCode: code,
        arbitratorName: name,
        panelName: formData.panelName.trim() || null,
        empanelledOn: formData.empanelledOn || null,
        email: formData.email.trim() || null,
        phoneNumber: formData.phoneNumber.trim() || null,
        statusCode: formData.statusCode,
      };

      if (mode === 'create' || mode === 'copy') {
        await arbitratorService.create(payload);
        notificationService.toast.success(`Arbitrator "${code}" created successfully.`);
      } else if (record) {
        await arbitratorService.update(record.id, { ...payload, rowVersion: record.rowVersion });
        notificationService.toast.success(`Arbitrator "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This arbitrator was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'An arbitrator with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save arbitrator. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'view' ? 'View Arbitrator' : mode === 'copy' ? 'Copy Arbitrator' : mode === 'create' ? 'Add Arbitrator' : 'Edit Arbitrator'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-grid-2">
            <div className="form-group" data-field="arbitratorCode">
              <label className="form-label">
                Arbitrator Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('arbitratorCode') ? ' form-field-error' : ''}`}
                value={formData.arbitratorCode}
                maxLength={30}
                placeholder="ARB-001"
                onChange={(e) => {
                  clearFieldError('arbitratorCode');
                  setFormData(prev => ({ ...prev, arbitratorCode: e.target.value.toUpperCase() }));
                }}
                disabled={isSaving}
              />
              {getFieldError('arbitratorCode') && (
                <div className="form-error-message">{getFieldError('arbitratorCode')}</div>
              )}
            </div>

            <div className="form-group" data-field="arbitratorName">
              <label className="form-label">
                Arbitrator Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('arbitratorName') ? ' form-field-error' : ''}`}
                value={formData.arbitratorName}
                maxLength={200}
                placeholder="Hon. Suresh Menon (Retd.)"
                onChange={(e) => {
                  clearFieldError('arbitratorName');
                  setFormData(prev => ({ ...prev, arbitratorName: e.target.value }));
                }}
                disabled={isSaving}
              />
              {getFieldError('arbitratorName') && (
                <div className="form-error-message">{getFieldError('arbitratorName')}</div>
              )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="panelName">
              <label className="form-label">Panel Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.panelName}
                maxLength={100}
                placeholder="IIAM Mumbai Panel"
                onChange={(e) => setFormData(prev => ({ ...prev, panelName: e.target.value }))}
                disabled={isSaving}
              />
            </div>

            <div className="form-group" data-field="empanelledOn">
              <label className="form-label">Empanelled On</label>
              <input
                type="date"
                className="form-input"
                value={formData.empanelledOn}
                onChange={(e) => setFormData(prev => ({ ...prev, empanelledOn: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="email">
              <label className="form-label">Email</label>
              <input
                type="email"
                className={`form-input${getFieldError('email') ? ' form-field-error' : ''}`}
                value={formData.email}
                maxLength={200}
                placeholder="arbitrator@example.com"
                onChange={(e) => {
                  clearFieldError('email');
                  setFormData(prev => ({ ...prev, email: e.target.value }));
                }}
                disabled={isSaving}
              />
              {getFieldError('email') && (
                <div className="form-error-message">{getFieldError('email')}</div>
              )}
            </div>

            <div className="form-group" data-field="phoneNumber">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-input"
                value={formData.phoneNumber}
                maxLength={20}
                placeholder="+91 98XXXXXXXX"
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(val) => setFormData(prev => ({
                ...prev,
                statusCode: ((val as string) || 'ACTIVE') as ArbitratorStatus,
              }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          (mode === 'create' || mode === 'copy')
            ? `Create arbitrator "${formData.arbitratorCode.trim().toUpperCase()}"?`
            : `Update arbitrator "${formData.arbitratorCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default ArbitratorFormModal;
